import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { TONE_CLASS } from '@/lib/status'
import { cn } from '@/lib/utils'
import { useAlerts } from '@/lib/queries'
import { useAttentionItems } from './AttentionStrip'
import { BrandMark, Wordmark } from './BrandMark'
import { BuildStamp } from './BuildStamp'
import { activeNav, NAV, navPathname, type NavItem } from './nav'
import { useLogout } from './session'
import { ThemeSwitch } from './ThemeSwitch'

const COLLAPSED_KEY = 'adfusion-console-sidebar-collapsed'

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

function writeCollapsed(v: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, v ? '1' : '0')
  } catch {
    // private mode or blocked storage: the sidebar just forgets
  }
}

/** A menu badge: a count and the tone it is drawn in. */
export interface AttentionBadge {
  n: number
  tone: 'failed' | 'publishing' | 'neutral' | 'scheduled'
}

/** The 오늘 badge: how many things to check, coloured by the worst of them. */
export function useAttentionBadge(): AttentionBadge {
  const items = useAttentionItems()
  const tone = items.some((i) => i.tone === 'failed') ? 'failed' : items.some((i) => i.tone === 'publishing') ? 'publishing' : 'neutral'
  return { n: items.length, tone }
}

/** The 알림 badge: unread alerts, in the "안 읽음" tone. */
export function useUnreadBadge(): AttentionBadge {
  const alerts = useAlerts()
  return { n: (alerts.data ?? []).filter((a) => !a.is_read).length, tone: 'scheduled' }
}

/** Both badges, for the sidebar and the phone menu. */
export function useNavBadges(): Record<NonNullable<NavItem['badge']>, AttentionBadge> {
  return { attention: useAttentionBadge(), unread: useUnreadBadge() }
}

function Badge({ badge, collapsed }: { badge: AttentionBadge; collapsed: boolean }) {
  if (badge.n <= 0) return null
  const c = TONE_CLASS[badge.tone]
  if (collapsed) {
    return <span aria-hidden className={cn('absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full', c.dot)} />
  }
  return (
    <span className={cn('ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-micro font-semibold tabular-nums', c.soft, c.text)}>
      {badge.n}
    </span>
  )
}

const NO_BADGE: AttentionBadge = { n: 0, tone: 'neutral' }

export function NavLinkItem({
  item,
  active,
  collapsed = false,
  badges,
  onNavigate,
}: {
  item: NavItem
  active: boolean
  collapsed?: boolean
  badges?: Partial<Record<NonNullable<NavItem['badge']>, AttentionBadge>>
  onNavigate?: () => void
}) {
  const Icon = item.icon
  const b = (item.badge && badges?.[item.badge]) || NO_BADGE
  const what = item.badge === 'unread' ? '안 읽음' : '확인할 것'
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? item.label : undefined}
      className={cn(
        'relative flex h-9 items-center gap-2.5 rounded-md px-2.5 text-body transition-colors',
        collapsed && 'justify-center px-0',
        active
          ? 'bg-sidebar-accent font-semibold text-sidebar-foreground'
          : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground',
      )}
    >
      <Icon size={16} aria-hidden className="shrink-0" />
      {collapsed ? <span className="sr-only">{item.label}</span> : <span className="truncate">{item.label}</span>}
      {b.n > 0 && collapsed && <span className="sr-only">, {what} {b.n}건</span>}
      <Badge badge={b} collapsed={collapsed} />
    </Link>
  )
}

function CollapseToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const label = collapsed ? '메뉴 펼치기' : '메뉴 접기'
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      title={label}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-sidebar-foreground"
    >
      {collapsed ? <PanelLeftOpen size={16} aria-hidden /> : <PanelLeftClose size={16} aria-hidden />}
    </button>
  )
}

/** The desktop sidebar: nine items in three groups, collapsible to icons. */
export function Sidebar({ className }: { className?: string }) {
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const active = activeNav(navPathname(useLocation()))
  const badges = useNavBadges()
  const logout = useLogout()
  const toggle = () => {
    setCollapsed((v) => {
      writeCollapsed(!v)
      return !v
    })
  }
  const groups = [0, 1, 2].map((g) => NAV.filter((i) => i.group === g))

  return (
    <aside
      aria-label="주 메뉴"
      className={cn(
        'sticky top-0 h-screen shrink-0 flex-col border-r border-border bg-sidebar text-sidebar-foreground transition-[width] duration-200',
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
        className,
      )}
    >
      <div className={cn('flex h-14 items-center gap-2 px-3', collapsed && 'justify-center px-0')}>
        <Link to="/" aria-label="Adfusion 콘솔, 오늘로" className={cn('flex min-w-0 items-center rounded-md p-1', !collapsed && 'flex-1')}>
          {collapsed ? <BrandMark size={24} /> : <Wordmark />}
        </Link>
        {!collapsed && <CollapseToggle collapsed={collapsed} onToggle={toggle} />}
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {groups.map((items, g) => (
          <ul key={g} className={cn('space-y-0.5', g > 0 && 'mt-2 border-t border-border pt-2')}>
            {items.map((item) => (
              <li key={item.to}>
                <NavLinkItem item={item} active={active?.to === item.to} collapsed={collapsed} badges={badges} />
              </li>
            ))}
          </ul>
        ))}
      </nav>

      <div className={cn('space-y-2 border-t border-border px-3 py-3', collapsed && 'flex flex-col items-center px-0')}>
        {collapsed && <CollapseToggle collapsed={collapsed} onToggle={toggle} />}
        <ThemeSwitch compact={collapsed} />
        {collapsed ? (
          <button
            type="button"
            onClick={() => void logout()}
            aria-label="로그아웃"
            title="로그아웃"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground"
          >
            <LogOut size={16} aria-hidden />
          </button>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <BuildStamp />
            <button
              type="button"
              onClick={() => void logout()}
              className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-meta text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground"
            >
              <LogOut size={14} aria-hidden />
              로그아웃
            </button>
          </div>
        )}
        {collapsed && <BuildStamp compact />}
      </div>
    </aside>
  )
}
